// Post-closing checklist and archive banner (ticket 192, D29). A funded loan shows four items;
// ticking the last one archives the loan. An archived loan is read-only; an owner or admin
// reopens it with a typed reason (the server says who may: `can_reopen`).
import React, { useEffect, useState } from 'react';
import { Archive, CheckSquare, Square } from 'lucide-react';
import { losApi } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { promptDialog } from '@/components/dialogs/DialogHost';
import { errorMessage } from '../../errors';
import { formatDate } from '../../kit/time';

const ARCHIVE_REASONS = {
  checklist: 'every post-closing item was ticked',
  timeout: 'the 60-day post-closing window ended',
  arive: 'Arive archived it',
};

export function PostClosingCard({ loan, onChange }) {
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const show = loan.status === 'funded' || !!loan.archived_at;

  useEffect(() => {
    if (!show) return undefined;
    let live = true;
    losApi.postClosing(loan.loan_file_id)
      .then((v) => { if (live) setView(v); })
      .catch(() => { if (live) setView(null); });
    return () => { live = false; };
  }, [loan.loan_file_id, loan.archived_at, loan.reopened_at, show]);

  if (!show || !view) return null;
  const archived = !!view.archived_at;

  const tick = async (item) => {
    setBusy(true);
    try {
      const r = await losApi.setPostClosingItem(loan.loan_file_id, item.key, !item.done);
      setView(r);
      if (r.archived) {
        toast({ title: 'Loan archived', description: 'Every post-closing item is done.' });
        await onChange();
      }
    } catch (e) {
      toast({ title: 'Could not update the checklist', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    const reason = await promptDialog({
      title: 'Reopen this archived loan?',
      description: 'It becomes editable again and gets a fresh 60-day post-closing window.',
      label: 'Reason (kept in the audit trail)',
      placeholder: 'Why this loan is being reopened',
      multiline: true,
      confirmText: 'Reopen loan',
    });
    if (!reason) return;
    setBusy(true);
    try {
      await losApi.reopenLoan(loan.loan_file_id, reason);
      toast({ title: 'Loan reopened' });
      await onChange();
    } catch (e) {
      toast({ title: 'Could not reopen', description: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 bg-white border border-slate-200 rounded-xl p-4" data-testid="post-closing-card">
      {archived ? (
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3" data-testid="archived-banner">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-700">
            <Archive size={15} />
            Archived {formatDate(view.archived_at)}: {ARCHIVE_REASONS[view.archived_reason] || view.archived_reason || 'archived'}. This loan is read-only.
          </div>
          {view.can_reopen && (
            <button type="button" onClick={reopen} disabled={busy} data-testid="reopen-loan"
              className="h-8 px-3 rounded-md text-[12px] font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
              Reopen
            </button>
          )}
        </div>
      ) : (
        <div className="text-[13px] font-semibold mb-2" style={{ color: '#030524' }}>
          Post-closing checklist
          {view.archive_on && <span className="font-normal text-slate-500"> · archives on {formatDate(view.archive_on)} or when every item is done</span>}
        </div>
      )}
      <ul className="space-y-1.5">
        {view.items.map((item) => (
          <li key={item.key}>
            <button type="button" disabled={busy || archived} onClick={() => tick(item)}
              data-testid={`post-closing-${item.key}`} aria-pressed={item.done}
              className="inline-flex items-center gap-2 text-[13px] text-slate-700 disabled:cursor-default">
              {item.done ? <CheckSquare size={16} style={{ color: '#6BA82E' }} /> : <Square size={16} className="text-slate-400" />}
              <span className={item.done ? 'line-through text-slate-500' : ''}>{item.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
